import dayjs from 'dayjs';
import { useState, useEffect } from 'react';

import { ASSESSMENT_TYPE } from '../../constants/assessments';
import assessmentTypeStorage from '../storage/assessmentTypeStorage';
import { getURLQueryParams } from '../navigation/navigation';
import { READING } from '../../constants/applications';
import { MCLASS_PORTAL_OWA_ACTIVE_TAB } from '../../constants/enrollment';

/**
 * Get current assessment type (BM or PM) based on where current date
 * falls in benchmark windows. If cannot be determined, fall back to BM.
 * @param {Array<Object>} bmWindows benchmark windows object
 * @return {String} current assessment type
 */
export const getAssessmentTypeByPeriod = bmWindows => {
    const currentDate = dayjs().format('YYYY-MM-DD');
    let predeterminedTimeIntervals = [
        {
            // interval from start of school year to end of BOY, BM type
            start: dayjs(currentDate).subtract(1, 'day'),
            end: dayjs(bmWindows[0].endDate), // BOY end date
            type: ASSESSMENT_TYPE.BM,
        },
        {
            // interval from end of BOY+1 to start of MOY-1, PM window
            start: dayjs(bmWindows[0].endDate).add(1, 'day'), // BOY end date +1
            end: dayjs(bmWindows[1].startDate).subtract(1, 'day'), // MOY start date -1
            type: ASSESSMENT_TYPE.PM,
        },
        {
            // interval from start of MOY to end of MOY, BM type
            start: dayjs(bmWindows[1].startDate), // MOY start date
            end: dayjs(bmWindows[1].endDate), // MOY end date
            type: ASSESSMENT_TYPE.BM,
        },
        {
            // interval from end of MOY+1 to start of EOY-1, PM type
            start: dayjs(bmWindows[1].endDate).add(1, 'day'), // MOY end date +1
            end: dayjs(bmWindows[2].startDate).subtract(1, 'day'), // EOY start date -1
            type: ASSESSMENT_TYPE.PM,
        },
        {
            // interval from start of EOY to end of EOY, BM type
            start: dayjs(bmWindows[2].startDate), // EOY start date
            end: dayjs(bmWindows[2].endDate), // EOY end date
            type: ASSESSMENT_TYPE.BM,
        },
        {
            // interval from end of EOY +1 to end of school year, PM type
            start: dayjs(bmWindows[2].endDate).add(1, 'day'), // EOY end date +1
            end: dayjs(currentDate).add(1, 'day'),
            type: ASSESSMENT_TYPE.PM,
        },
    ];

    // 4th period support
    if (bmWindows.length > 3) {
        predeterminedTimeIntervals = [
            // remove last interval in case it should be modified
            // with 4th period data
            ...predeterminedTimeIntervals.slice(0, -1),
            {
                // interval from end of EOY+1 to start of SUMMER-1, PM type
                start: dayjs(bmWindows[2].endDate).add(1, 'day'), // MOY end date +1
                end: dayjs(bmWindows[3].startDate).subtract(1, 'day'), // EOY start date -1
                type: ASSESSMENT_TYPE.PM,
            },
            {
                // interval from start of SUMMER to end of SUMMER, BM type
                start: dayjs(bmWindows[3].startDate), // EOY start date
                end: dayjs(bmWindows[3].endDate), // EOY end date
                type: ASSESSMENT_TYPE.BM,
            },
            {
                // interval from end of SUMMER +1 to end of school year, PM type
                start: dayjs(bmWindows[3].endDate).add(1, 'day'), // SUMMER end date +1
                end: dayjs(currentDate).add(1, 'day'),
                type: ASSESSMENT_TYPE.PM,
            },
        ];
    }

    let activeAssessmentType = ASSESSMENT_TYPE.BM;

    const currentInterval = predeterminedTimeIntervals.find(
        interval =>
            interval.start.diff(currentDate, 'day') <= 0 &&
            interval.end.diff(currentDate, 'day') >= 0,
    );

    activeAssessmentType = currentInterval.type;

    return activeAssessmentType;
};

/**
 * Get default assessment type (BM or PM) for application group.
 * Rules or precedence:
 * 1. value in persistent storage, selected by the user
 * 2. value based on assessment windows dates
 * 3. BM
 *
 * @param {Array<Object>} bmWindows benchmark windows object
 * @param {String} applicationGroup active application group
 * @return {String} current assessment type
 */
export const getDefalutAssessmentType = (bmWindows, applicationGroup) => {
    const typeFromStorage = assessmentTypeStorage.read(applicationGroup);
    const typeFromLocalStorage = localStorage.getItem(MCLASS_PORTAL_OWA_ACTIVE_TAB);
    const { activeTab } = getURLQueryParams();

    if (activeTab && applicationGroup === READING) {
        return activeTab;
    }

    if (typeFromLocalStorage && applicationGroup === READING) {
        return typeFromLocalStorage;
    }

    if (typeFromStorage) {
        return typeFromStorage;
    }
    if (bmWindows) {
        return getAssessmentTypeByPeriod(bmWindows);
    }
    return ASSESSMENT_TYPE.BM;
};

/**
 * Custom hook to sync state value of current assessment type (BM or PM) with session storage
 * @param {Array<Object>} bmWindows benchmark windows object
 * @param {String} applicationGroup active application group
 * @return {Array} [type, setType] -> [value, setValue] interface just like for useState
 */
const useAppGroupAssessmentType = (bmWindows, applicationGroup) => {
    const [type, setType] = useState(() => getDefalutAssessmentType(bmWindows, applicationGroup));

    useEffect(() => {
        assessmentTypeStorage.update(applicationGroup, type);
    }, [applicationGroup, type]);

    return [type, setType];
};

export default useAppGroupAssessmentType;
